import {
  Asset,
  AssetsDebts,
  ConsentOrdersBase,
  ConsentOrdersPathName,
  Debt,
  InitialDetails,
  toast
} from "@fla/components";
import { useAppDispatch } from "app/store";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from "firebase/firestore";
import { FirebaseRefs, SliceStatus } from "interfaces";
import { consentOrdersActions } from "pages/ConsentOrders";
import { selectConsentOrdersBase } from "pages/ConsentOrders/consentOrdersSelectors";
import { selectUserProfile } from "pages/User";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { FirestoreService } from "services";
import { db } from "utils";

export function useConsentOrders() {
  const navigation = useNavigate();
  const location = useLocation() as { state: { parent: Asset | Debt } };

  const dispatch = useAppDispatch();

  const [submitStatus, setSubmitStatus] = useState<SliceStatus>(SliceStatus.Idle);
  const [deletingStatus, setDeletingStatus] = useState<SliceStatus>(SliceStatus.Idle);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);

  const user = useSelector(selectUserProfile);
  const { assetsDebts, initialDetails, submitted } = useSelector(selectConsentOrdersBase);

  const getAssets = async () => {
    if (!user?.uid) return [];
    const q = query(collection(db, FirebaseRefs.ASSETS), where("uid", "==", user.uid));

    const querySnapshot = await getDocs(q);
    const assets: Asset[] = [];
    querySnapshot.forEach(doc => {
      assets.push({ id: doc.id, ...doc.data() } as Asset);
    });

    return assets;
  };

  const addAsset = async (data: Asset) => {
    try {
      const { id = "", ...rest } = data;
      setSubmitStatus(SliceStatus.Pending);

      if (!!id) {
        let payload = {
          ...rest,
          updatedAt: serverTimestamp()
        };

        const updateRef = await doc(db, FirebaseRefs.ASSETS, id);
        await updateDoc(updateRef, payload);
        dispatch(consentOrdersActions.updateAsset(payload));
      } else {
        let payload = {
          ...rest,
          uid: user!.uid,
          createdAt: serverTimestamp()
        };

        await addDoc(collection(db, FirebaseRefs.ASSETS), payload);
        setSubmitStatus(SliceStatus.Resolved);
        dispatch(consentOrdersActions.addAsset(payload));
      }

      navigation(window.location.pathname.replace("/add", ""), {
        state: location.state?.parent
      });
    } catch (error: any) {
      toast({
        type: "error",
        message: error?.message || "Something went wrong"
      });
      setSubmitStatus(SliceStatus.Rejected);
    }
  };

  const deleteAsset = async (id: string) => {
    try {
      setDeletingStatus(SliceStatus.Pending);
      await deleteDoc(doc(db, FirebaseRefs.ASSETS, id));
      setDeletingStatus(SliceStatus.Resolved);
      navigation(window.location.pathname.replace("/add", ""), {
        state: location.state?.parent
      });
    } catch (error: any) {
      toast({
        type: "error",
        message: error?.message || "Something went wrong"
      });
      setDeletingStatus(SliceStatus.Rejected);
    }
  };

  const addDebt = async (data: Debt) => {
    try {
      const { id = "", ...rest } = data;
      setSubmitStatus(SliceStatus.Pending);

      if (!!id) {
        let payload = {
          ...rest,
          updatedAt: serverTimestamp()
        };

        const updateRef = await doc(db, FirebaseRefs.DEBTS, id);
        await updateDoc(updateRef, payload);
        dispatch(consentOrdersActions.updateDebt(payload));
      } else {
        let payload = {
          ...rest,
          uid: user!.uid,
          createdAt: serverTimestamp()
        };

        await addDoc(collection(db, FirebaseRefs.DEBTS), payload);
        setSubmitStatus(SliceStatus.Resolved);

        dispatch(consentOrdersActions.addDebt(payload));
      }
      navigation(window.location.pathname.replace("/add", ""), {
        state: location.state?.parent
      });
    } catch (error: any) {
      toast({
        type: "error",
        message: error?.message || "Something went wrong"
      });
      setSubmitStatus(SliceStatus.Rejected);
    }
  };

  const deleteDebt = async (id: string) => {
    await deleteDoc(doc(db, FirebaseRefs.DEBTS, id));
    navigation(window.location.pathname.replace("/add", ""), {
      state: location.state?.parent
    });
  };

  const getDebts = async () => {
    const q = query(collection(db, FirebaseRefs.DEBTS), where("uid", "==", user!.uid));

    const querySnapshot = await getDocs(q);
    const debts: Debt[] = [];
    querySnapshot.forEach(doc => {
      debts.push({ id: doc.id, ...doc.data() } as Debt);
    });

    return debts;
  };

  /** --------------------------------------------------------- */
  const getData = async (coll: FirebaseRefs) => {
    const docRef = doc(db, coll, user!.email.trim());
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      return {};
    }
  };

  const getConsentOrdersBase = async () => {
    try {
      const data = (await getData(FirebaseRefs.ConsentOrders)) as ConsentOrdersBase;
      return {
        status: true,
        data
      };
    } catch (error) {
      console.error("consent orders base", error);
      return { status: false };
    }
  };

  const submitConsentOrder = async () => {
    try {
      setSubmitStatus(SliceStatus.Pending);
      const updateRef = await doc(db, FirebaseRefs.ConsentOrders, user!.email.trim());
      await updateDoc(updateRef, {
        submitted: true,
        updatedAt: serverTimestamp()
      });
      setShowSuccessModal(true);
      setSubmitStatus(SliceStatus.Resolved);

      // const details = queryClient.getQueryData(firebaseRefs.consent_orders);

      // MailService.sendMkiEmail({
      //   user: {
      //     ...(details?.initial_details?.user || {}),
      //     phone: details?.initial_details?.user?.mobile ?? ""
      //   },
      //   subject:
      //     process.env.REACT_APP_NODE_ENV !== "production"
      //       ? `FLA Consent Orders Submitted [${process.env.REACT_APP_NODE_ENV?.toUpperCase()}]`
      //       : `FLA Consent Orders Submitted`
      // });

      // const payload = MailService.prepareDocupilotAPIBody(details?.initial_details || {});

      // const templateIds = [
      //   process.env.REACT_APP_DOCUPILOT_CONSENT_ORDERS_WA_TEMPLATE_ID,
      //   process.env.REACT_APP_DOCUPILOT_CONSENT_ORDERS_ES_TEMPLATE_ID
      // ];

      // for (const templateId of templateIds) {
      //   MailService.sendDocFile({ ...payload, environment: process.env.REACT_APP_NODE_ENV?.toUpperCase() }, templateId);
      // }
    } catch (error) {
      setSubmitStatus(SliceStatus.Rejected);
    }
  };

  const saveInitialDetails = async ({
    currentStep,
    data,
    isFirst,
    isLast
  }: {
    currentStep: number;
    data: InitialDetails;
    isFirst: boolean;
    isLast: boolean;
  }) => {
    try {
      setSubmitStatus(SliceStatus.Pending);
      const _timestamp = serverTimestamp();
      const isUpdate = initialDetails?.currentStep >= currentStep;

      const payload = {
        initialDetails: {
          ...data,
          currentStep,
          isCompleted: isLast,
          ...(isFirst && !isUpdate ? { createdAt: _timestamp } : {}),
          updatedAt: _timestamp
        },
        uid: user?.uid,
        submitted: false
      };

      if (isFirst) {
        await FirestoreService.setDocument({
          coll: FirebaseRefs.ConsentOrders,
          docId: user!.email.trim(),
          data: payload
        });
      } else {
        await FirestoreService.updateDocument({
          coll: FirebaseRefs.ConsentOrders,
          docId: user!.email.trim(),
          data: payload
        });
      }

      setSubmitStatus(SliceStatus.Resolved);
      return true;
    } catch (error: any) {
      console.error(error);
      toast({
        message: error?.message || "Something went wrong!",
        type: "error"
      });
      setSubmitStatus(SliceStatus.Rejected);
      return false;
    }
  };

  /**
   *
  //  */
  const updateAssetsDebts = async (data: Partial<AssetsDebts> & { currentStep: number }) => {
    const updateUsersRef = await doc(db, FirebaseRefs.ConsentOrders, user!.email.trim());
    await updateDoc(updateUsersRef, {
      assetsDebts: {
        ...assetsDebts,
        ...data
      }
    });
  };

  const isSubmitting = submitStatus === SliceStatus.Pending;
  const isDeleting = deletingStatus === SliceStatus.Pending;

  // Progress And Redirection

  const consentOrderCurrentStep = initialDetails?.currentStep ?? 1;
  const consentOrderLastStep = initialDetails?.children?.if_children === "no" ? 14 : 15;

  const isConsentOrderCompleted = initialDetails?.isCompleted ?? false;

  const isConsentOrderSubmitted = submitted ?? false;

  const isAssetsDebtsCompleted = () => {
    return (
      assetsDebts?.real_estate?.isCompleted &&
      assetsDebts?.super?.isCompleted &&
      assetsDebts?.bank?.isCompleted &&
      assetsDebts?.vehicle?.isCompleted &&
      assetsDebts?.investments?.isCompleted &&
      assetsDebts?.business?.isCompleted &&
      assetsDebts?.overseas?.isCompleted &&
      assetsDebts?.furniture?.isCompleted &&
      assetsDebts?.debts?.isCompleted &&
      assetsDebts?.additional_requirements?.isCompleted &&
      assetsDebts?.summary?.isCompleted
    );
  };

  const getCurrentAssetsDebtsStep = () => {
    let current = 0;

    if (assetsDebts?.real_estate?.isCompleted) current += 1;
    if (assetsDebts?.super?.isCompleted) current += 1;
    if (assetsDebts?.bank?.isCompleted) current += 1;
    if (assetsDebts?.vehicle?.isCompleted) current += 1;
    if (assetsDebts?.investments?.isCompleted) current += 1;
    if (assetsDebts?.business?.isCompleted) current += 1;
    if (assetsDebts?.overseas?.isCompleted) current += 1;
    if (assetsDebts?.furniture?.isCompleted) current += 1;
    if (assetsDebts?.debts?.isCompleted) current += 1;
    if (assetsDebts?.additional_requirements?.isCompleted) current += 1;
    if (assetsDebts?.summary?.isCompleted) current += 1;
    return current;
  };

  const getTargetRoute = () => {
    if (!Object.keys(initialDetails).length) {
      navigation(ConsentOrdersPathName.Welcome);
      return;
    }
    if (isConsentOrderCompleted && consentOrderCurrentStep !== consentOrderLastStep) {
      navigation(ConsentOrdersPathName.InitialDetails);
      return;
    }
    if (!isConsentOrderCompleted) {
      navigation(ConsentOrdersPathName.InitialDetails);
      return;
    } else if (isConsentOrderCompleted && !assetsDebts?.real_estate?.isCompleted) {
      const payload = {
        currentStep: 1
      };
      dispatch(consentOrdersActions.setAssetsDebts(payload));
      navigation(ConsentOrdersPathName.RealEstate);
      return;
    } else if (isConsentOrderCompleted && !assetsDebts?.super?.isCompleted) {
      const payload = {
        currentStep: 2
      };
      dispatch(consentOrdersActions.setAssetsDebts(payload));

      navigation(ConsentOrdersPathName.Superannuation);
      return;
    } else if (isConsentOrderCompleted && !assetsDebts?.bank?.isCompleted) {
      const payload = {
        currentStep: 3
      };
      dispatch(consentOrdersActions.setAssetsDebts(payload));

      navigation(ConsentOrdersPathName.Bank);
      return;
    } else if (isConsentOrderCompleted && !assetsDebts?.vehicle?.isCompleted) {
      const payload = {
        currentStep: 4
      };
      dispatch(consentOrdersActions.setAssetsDebts(payload));

      navigation(ConsentOrdersPathName.Vehicle);
      return;
    } else if (isConsentOrderCompleted && !assetsDebts?.investments?.isCompleted) {
      const payload = {
        currentStep: 5
      };
      dispatch(consentOrdersActions.setAssetsDebts(payload));

      navigation(ConsentOrdersPathName.Investments);
      return;
    } else if (isConsentOrderCompleted && !assetsDebts?.business?.isCompleted) {
      const payload = {
        currentStep: 6
      };
      dispatch(consentOrdersActions.setAssetsDebts(payload));

      navigation(ConsentOrdersPathName.Business);
      return;
    } else if (isConsentOrderCompleted && !assetsDebts?.overseas?.isCompleted) {
      const payload = {
        currentStep: 7
      };
      dispatch(consentOrdersActions.setAssetsDebts(payload));

      navigation(ConsentOrdersPathName.Overseas);
      return;
    } else if (isConsentOrderCompleted && !assetsDebts?.furniture?.isCompleted) {
      const payload = {
        currentStep: 8
      };
      dispatch(consentOrdersActions.setAssetsDebts(payload));

      navigation(ConsentOrdersPathName.Furniture);
      return;
    } else if (isConsentOrderCompleted && !assetsDebts?.debts?.isCompleted) {
      const payload = {
        currentStep: 9
      };
      dispatch(consentOrdersActions.setAssetsDebts(payload));

      navigation(ConsentOrdersPathName.Debts);
      return;
    } else if (isConsentOrderCompleted && !assetsDebts?.additional_requirements?.isCompleted) {
      const payload = {
        currentStep: 10
      };
      dispatch(consentOrdersActions.setAssetsDebts(payload));

      navigation(ConsentOrdersPathName.AdditionalRequirements);
      return;
    } else if (isConsentOrderCompleted && !assetsDebts?.summary?.isCompleted) {
      const payload = {
        currentStep: 11
      };
      dispatch(consentOrdersActions.setAssetsDebts(payload));

      navigation(ConsentOrdersPathName.Summary);
      return;
    } else {
      navigation(ConsentOrdersPathName.Submit);
      return;
    }
  };

  const totalProgressStep = () => consentOrderLastStep + 1 + 11;
  const currentProgressStep = () => {
    let current = 0;

    current += getCurrentAssetsDebtsStep();
    if (isConsentOrderSubmitted) current += 1;
    if (initialDetails?.currentStep) current += initialDetails?.currentStep;
    return current;
  };

  return {
    submitConsentOrder,
    updateAssetsDebts,
    addAsset,
    deleteAsset,
    addDebt,
    deleteDebt,
    isDeleting,
    isSubmitting,
    showSuccessModal,
    setShowSuccessModal,
    saveInitialDetails,
    getData,
    getAssets,
    getDebts,
    getConsentOrdersBase,

    // Progress & Redirection
    isAssetsDebtsCompleted,
    getTargetRoute,
    consentOrderCurrentStep,
    consentOrderLastStep,
    isConsentOrderCompleted,
    isConsentOrderSubmitted,
    totalProgressStep,
    currentProgressStep,
    getCurrentAssetsDebtsStep
  };
}
